/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { getMe } from './lib/api'
import { getAccessToken, logoutSession, refreshSession, subscribeSession } from './lib/session'
import type { PlatformUser } from './types'

type AuthState = {
  status: 'loading' | 'authenticated' | 'anonymous'
  user: PlatformUser | null
  reload: () => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthState['status']>('loading')
  const [user, setUser] = useState<PlatformUser | null>(null)
  const queryClient = useQueryClient()

  async function reload() {
    setStatus('loading')
    const token = getAccessToken() || await refreshSession()
    if (!token) { setUser(null); setStatus('anonymous'); return }
    try { setUser(await getMe()); setStatus('authenticated') }
    catch { setUser(null); setStatus('anonymous') }
  }

  useEffect(() => { void reload() }, [])
  useEffect(() => subscribeSession(() => {
    if (!getAccessToken()) { setUser(null); setStatus('anonymous'); queryClient.clear() }
  }), [queryClient])

  const value = useMemo<AuthState>(() => ({
    status, user, reload,
    signOut: async () => { await logoutSession(); queryClient.clear(); setUser(null); setStatus('anonymous') },
  }), [status, user, queryClient])
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth deve ser usado dentro de AuthProvider')
  return value
}

export function can(user: PlatformUser | null, capability: string) {
  return Boolean(user?.capabilities.includes(capability))
}
